import { AlertCircle, Loader2, Plus, X } from "lucide-react";
import { useEffect, useRef, useState } from "react";
import { api } from "../../api/client";

interface AddKeywordModalProps {
	open: boolean;
	onClose: () => void;
	onCreated: () => void;
}

export const AddKeywordModal = ({
	open,
	onClose,
	onCreated,
}: AddKeywordModalProps) => {
	const [keyword, setKeyword] = useState("");
	const [saving, setSaving] = useState(false);
	const [error, setError] = useState<string | null>(null);
	const inputRef = useRef<HTMLInputElement>(null);

	// Focus the input when modal opens
	useEffect(() => {
		if (open) {
			setTimeout(() => inputRef.current?.focus(), 50);
		}
	}, [open]);

	if (!open) return null;

	const handleSubmit = async (e: React.FormEvent) => {
		e.preventDefault();
		const trimmed = keyword.trim();
		if (!trimmed) return;

		setSaving(true);
		setError(null);
		try {
			const { data, error: err } = await api["category-mappings"].post({
				keyword: trimmed,
			});
			const json = (data ?? err) as {
				success?: boolean;
				error?: string;
			} | null;
			if (err) {
				setError(
					(json as { error?: string })?.error ?? "Failed to create keyword",
				);
				return;
			}
			setKeyword("");
			setError(null);
			onCreated();
			onClose();
		} catch {
			setError("Network error — please try again");
		} finally {
			setSaving(false);
		}
	};

	const handleClose = () => {
		if (saving) return;
		setKeyword("");
		setError(null);
		onClose();
	};

	return (
		<div className="fixed inset-0 z-50 flex items-center justify-center">
			{/* Backdrop */}
			{/* biome-ignore lint/a11y/useSemanticElements: backdrop overlay */}
			<div
				role="button"
				tabIndex={-1}
				className="absolute inset-0 bg-black/40 backdrop-blur-sm"
				onClick={handleClose}
				onKeyDown={() => {}}
			/>

			{/* Modal */}
			<div className="relative bg-white rounded-2xl shadow-2xl w-full max-w-md mx-4 overflow-hidden">
				{/* Header */}
				<div className="flex items-center justify-between px-6 py-4 border-b border-slate-100">
					<div className="flex items-center gap-2.5">
						<div className="w-8 h-8 rounded-lg bg-primary/10 flex items-center justify-center">
							<Plus className="w-4 h-4 text-primary" />
						</div>
						<h3 className="text-lg font-semibold text-slate-800">
							Add Keyword
						</h3>
					</div>
					<button
						type="button"
						onClick={handleClose}
						disabled={saving}
						className="p-2 rounded-lg text-slate-400 hover:text-slate-600 hover:bg-slate-100 transition-colors"
					>
						<X className="w-5 h-5" />
					</button>
				</div>

				{/* Body */}
				<form onSubmit={handleSubmit} className="p-6 space-y-4">
					<div>
						<label
							htmlFor="new-keyword"
							className="block text-sm font-medium text-slate-700 mb-1.5"
						>
							Keyword
						</label>
						<input
							ref={inputRef}
							id="new-keyword"
							type="text"
							value={keyword}
							onChange={(e) => {
								setKeyword(e.target.value);
								if (error) setError(null);
							}}
							placeholder="e.g. bluetooth speaker"
							disabled={saving}
							className="w-full px-4 py-2.5 rounded-xl bg-slate-50 border border-slate-200/80 text-sm text-slate-700 placeholder:text-slate-400 focus:outline-none focus:ring-2 focus:ring-primary/20 focus:border-primary/30 focus:bg-white transition-all disabled:opacity-50"
						/>
						<p className="text-[11px] text-slate-400 mt-1.5">
							The keyword will be added as an unmapped entry so you can assign a
							category later.
						</p>
					</div>

					{/* Error */}
					{error && (
						<div className="flex items-center gap-2 px-3 py-2.5 rounded-xl bg-danger/5 border border-danger/20">
							<AlertCircle className="w-4 h-4 text-danger shrink-0" />
							<p className="text-sm text-danger">{error}</p>
						</div>
					)}

					{/* Actions */}
					<div className="flex items-center justify-end gap-3 pt-2">
						<button
							type="button"
							onClick={handleClose}
							disabled={saving}
							className="px-4 py-2.5 rounded-xl text-sm font-medium text-slate-600 hover:bg-slate-100 transition-colors disabled:opacity-50"
						>
							Cancel
						</button>
						<button
							type="submit"
							disabled={saving || !keyword.trim()}
							className="flex items-center gap-2 px-5 py-2.5 rounded-full bg-brand text-black text-sm font-semibold hover:bg-brand-hover transition-colors disabled:opacity-50 disabled:pointer-events-none"
						>
							{saving ? (
								<Loader2 className="w-4 h-4 animate-spin" />
							) : (
								<Plus className="w-4 h-4" />
							)}
							Add Keyword
						</button>
					</div>
				</form>
			</div>
		</div>
	);
};
